/**
 * Core translation helpers that follow proper react-i18next patterns.
 * These utilities can be used anywhere (client, server, common code).
 * Uses count parameter for pluralization with common.json time structure.
 * 
 * Note: For client-specific helpers, see client/utils/i18n/translationHelpers.ts
 */

import { getHumanTimeData, HumanTimeData } from './humanTimeTranslated';
import { ElementType } from '../../elements/elementTypes';
import { chooseSingular, pluralise } from '../stringUtil';

/**
 * Helper to pluralize English element type names.
 * Handles special cases that the generic pluralise utility doesn't cover.
 * Note - this is only for fallback English text, not for translations.
 */
const pluraliseElementType = (word: string, count: number): string => {
    if (count === 1) return word;
    
    // Special case: words ending in 'tch' need 'es' not just 's'
    if (word.endsWith('swatch')) {
        return word + 'es';
    }
    
    // Default: use standard pluralise utility
    return pluralise(word, count);
};

// Simple type for react-i18next t function
export type ReactI18nextTranslationFunction = (key: string, options?: { count?: number; [key: string]: unknown }) => string;

// Export the time data type for backward compatibility
export type HumanTimeResult = HumanTimeData;

// Legacy type for generic translation function
export type TranslateFunction = (key: string, defaultValue: string, context?: Record<string, unknown>) => string;

/**
 * Map element type enum values to their translation keys.
 * This provides a single source of truth for element type to translation key mapping.
 */
const createElementTypeToTranslationKeyMap = (): Record<string, string> => {
    // Generate base mapping from ElementType enum values to translation keys
    const mapping: Record<string, string> = {
        [ElementType.ALIAS_TYPE]: 'alias',           
        [ElementType.BOARD_TYPE]: 'board',          
        [ElementType.COLUMN_TYPE]: 'column',        
        [ElementType.CARD_TYPE]: 'card',             
        [ElementType.LINK_TYPE]: 'link',             
        [ElementType.LINE_TYPE]: 'line',             
        [ElementType.IMAGE_TYPE]: 'image',           
        [ElementType.FILE_TYPE]: 'file',             
        [ElementType.DOCUMENT_TYPE]: 'document',     
        [ElementType.TABLE_TYPE]: 'table',           
        [ElementType.CLONE_TYPE]: 'clone',           
        [ElementType.SKETCH_TYPE]: 'sketch',         
        [ElementType.ANNOTATION_TYPE]: 'annotation', 
        [ElementType.TASK_TYPE]: 'task',             
        [ElementType.TASK_LIST_TYPE]: 'taskList',    
        [ElementType.COLOR_SWATCH_TYPE]: 'colorSwatch', 
        [ElementType.COMMENT_THREAD_TYPE]: 'commentThread', 
        [ElementType.SKELETON_ELEMENT_TYPE]: 'skeleton', 
        [ElementType.UNKNOWN_ELEMENT_TYPE]: 'unknown',
    };
    
    // Add enum names (like 'CARD_TYPE') mapping to their translation keys
    Object.entries(ElementType).forEach(([enumName, enumValue]) => {
        if (mapping[enumValue]) {
            mapping[enumName] = mapping[enumValue];
        }
    });

    return mapping;
};

const ELEMENT_TYPE_TRANSLATION_MAP = createElementTypeToTranslationKeyMap();

/**
 * Normalizes an element type string by converting to lowercase and removing underscores.
 */
const normalizeElementType = (elementType: string): string => {
    return elementType.toLowerCase().replace(/_/g, '');
};

/**
 * Converts underscore-delimited string to camelCase.
 * Examples: 'task_list' → 'taskList', 'color_swatch' → 'colorSwatch'
 */
const toCamelCase = (str: string): string => {
    return str.toLowerCase().replace(/_([a-z])/g, (_, letter) => letter.toUpperCase());
};

/**
 * Get the translation key for an element type.
 * This provides a single source of truth for element type to translation key mapping.
 * Handles enum values, normalized versions, and provides fallbacks.
 */
export const getElementTypeTranslationKey = (elementType: string): string => {
    // Direct lookup - handles enum values and names
    if (ELEMENT_TYPE_TRANSLATION_MAP[elementType]) 
    {
        return ELEMENT_TYPE_TRANSLATION_MAP[elementType];
    }

    // Check if input matches any existing translation key (already camelCase)
    const translationKeys = Object.values(ELEMENT_TYPE_TRANSLATION_MAP);
    if (translationKeys.includes(elementType)) {
        return elementType;
    }

    // Try camelCase conversion for underscore-delimited strings
    const camelCaseInput = toCamelCase(elementType);
    if (translationKeys.includes(camelCaseInput)) {
        return camelCaseInput;
    }
    
    // Check if any translation key, when normalized, matches our normalized input
    const normalizedInput = normalizeElementType(elementType);
    for (const translationKey of translationKeys) {
        if (normalizeElementType(translationKey) === normalizedInput) {
            return translationKey;
        }
    }
    
    // Fallback: normalize input (lowercase, no underscores)
    return normalizedInput;
};

/**
 * Translates human time using react-i18next with proper pluralization support.
 * Uses the common.json time structure with _one/_other suffix format.
 * 
 * @param t - react-i18next translation function
 * @param seconds - Number of seconds or Date object
 * @returns Translated time string
 */
export const translateHumanTime = (t: ReactI18nextTranslationFunction, seconds: number | Date): string => {
    // Convert Date objects to seconds like the original humanTime.js does
    let secondsValue: number;
    if (seconds instanceof Date) {
        secondsValue = Math.round((Date.now() - seconds.getTime()) / 1000);
    } else {
        secondsValue = seconds;
    }
    
    const timeData = getHumanTimeData(secondsValue);
    
    if (timeData.type === 'justNow') {
        const key = 'time.suffixes.justNow';
        return t(key, { defaultValue: 'just now' });
    }
    
    if (timeData.type === 'time' && timeData.unit && timeData.value !== undefined) {
        // Use react-i18next pluralization with _one/_other suffix format
        // This matches the common.json structure with proper pluralization support
        const baseKey = `time.units.${timeData.unit}`;
        const pluralSuffix = timeData.isPlural ? '_other' : '_one';
        const key = `${baseKey}${pluralSuffix}`;
        const englishUnit = timeData.isPlural ? `${timeData.unit}s` : timeData.unit;
        const translatedUnit = t(key, { defaultValue: englishUnit });
        
        return `${timeData.value} ${translatedUnit}`;
    }
    
    return 'just now'; // fallback
};

/**
 * Translates element types using react-i18next pluralization.
 * 
 * @param t - react-i18next translation function
 * @param elementType - The element type to translate
 * @param count - Number of elements (for pluralization)
 * @returns Translated element type string
 */
export const translateElementType = (t: ReactI18nextTranslationFunction, elementType: string, count: number = 1): string => {
    // Handle special case for document_short variant
    if (elementType === 'document_short') {
        const pluralKey = count === 1 ? 'document_short_one' : 'document_short_other';
        const fullKey = `elementTypes.document.${pluralKey}`;
        const fallback = chooseSingular(count, 'doc', 'docs');
        return t(fullKey, { defaultValue: fallback });
    }
    
    // Use the centralized mapping to get the translation key
    const translationKey = getElementTypeTranslationKey(elementType);
    
    // Convert camelCase to spaced English (e.g., 'taskList' → 'task list')
    const baseEnglish = translationKey.replace(/([A-Z])/g, ' $1').toLowerCase().trim();
    
    // Use existing pluralization utility for fallback text (with special cases handled)
    const englishFallback = pluraliseElementType(baseEnglish, count);
    
    // Use the i18next v4 format: elementTypes.card.card_one / elementTypes.card.card_other
    const pluralKey = count === 1 ? `${translationKey}_one` : `${translationKey}_other`;
    const fullKey = `elementTypes.${translationKey}.${pluralKey}`;
    
    return t(fullKey, { defaultValue: englishFallback });
};

/**
 * Formats a duration (in milliseconds) using the common.json time units.
 *
 * @param translate - Translation function
 * @param milliseconds - Duration to format
 */
export const formatDurationFromMilliseconds = (
    translate: TranslateFunction,
    milliseconds?: number | null,
): string => {
    if (!milliseconds || milliseconds <= 0) return '';

    const seconds = Math.max(1, Math.round(milliseconds / 1000));
    const timeData = getHumanTimeData(seconds);

    if (timeData.type !== 'time' || !timeData.unit || timeData.value === undefined) {
        return '';
    }

    const pluralKey = timeData.isPlural ? '_other' : '_one';
    const translationKey = `time.units.${timeData.unit}${pluralKey}`;
    const fallbackUnit = timeData.isPlural ? `${timeData.unit}s` : timeData.unit;
    const translatedUnit = translate(translationKey, fallbackUnit);

    if (!translatedUnit) return '';
    if (timeData.isPlural) {
        return `${timeData.value} ${translatedUnit}`;
    }

    return translatedUnit;
};

/**
 * Translates a list of child element types into a human-readable format.
 * Uses proper react-i18next patterns with count parameter.
 * 
 * @param t - react-i18next translation function
 * @param childCounts - Object mapping element types to their counts
 * @param useConjunction - Whether to use conjunction (e.g., "and") or just comma-separate (default: false)
 * @returns Translated description string
 */
export const translateChildDescription = (
    t: ReactI18nextTranslationFunction, 
    childCounts: Record<string, number>,
    useConjunction: boolean = false
): string => {
    const descriptions: string[] = [];
    
    Object.entries(childCounts).forEach(([type, count]) => {
        if (count > 0) {
            const translatedType = translateElementType(t, type, count);
            descriptions.push(`${count} ${translatedType}`);
        }
    });
    
    if (descriptions.length === 0) {
        const key = 'meta.empty';
        return t(key, { defaultValue: 'empty' });
    }
    
    if (descriptions.length === 1) {
        return descriptions[0];
    }
    
    // If not using conjunction, just join with commas
    if (!useConjunction) {
        return descriptions.join(', ');
    }
    
    // Handle multiple items with proper conjunction
    const lastItem = descriptions.pop()!;
    const firstItems = descriptions.join(', ');
    
    const key = 'meta.conjunction';
    const fallback = `${firstItems} and ${lastItem}`;
    return t(key, {
        items: firstItems,
        lastItem: lastItem,
        defaultValue: fallback
    });
};

/**
 * Formats time with optional suffix using react-i18next.
 * 
 * @param t - react-i18next translation function
 * @param seconds - Number of seconds or Date object
 * @param withSuffix - Whether to include "ago" or "from now" suffix
 * @returns Formatted time string with optional suffix
 */
export const formatTimeWithSuffix = (
    t: ReactI18nextTranslationFunction, 
    seconds: number | Date, 
    withSuffix: boolean = true
): string => {
    // Convert Date objects to seconds like the original humanTime.js does
    let secondsValue: number;
    if (seconds instanceof Date) {
        secondsValue = Math.round((Date.now() - seconds.getTime()) / 1000);
    } else {
        secondsValue = seconds;
    }
    
    const timeData = getHumanTimeData(secondsValue);
    const timeString = translateHumanTime(t, seconds);
    
    if (!withSuffix || timeData.type === 'justNow') {
        return timeString;
    }
    
    if (timeData.type === 'time') {
        const suffixKey = timeData.isFromFuture ? 'time.suffixes.fromNow' : 'time.suffixes.ago';
        const defaultSuffix = timeData.isFromFuture ? 'from now' : 'ago';
        const suffix = t(suffixKey, { defaultValue: defaultSuffix });
        
        return `${timeString} ${suffix}`;
    }
    
    return timeString;
};
